/**
 * Operator calls of the job inspector and the feedback queue (ADR 0216 and
 * 0217). They use the operator session cookie, like the operations snapshot,
 * and never the Mini App `/api/v1` session.
 */

export interface ToolShape {
  name: string;
  arg_shape: Record<string, string>;
  status: string;
  row_count: number | null;
  bytes: number;
}

interface ModelCall {
  attempt: number;
  tokens_in: number | null;
  tokens_out: number | null;
  wall_ms: number;
}

export interface TurnTrace {
  n: number;
  prompt_version: string;
  blocks: string[];
  prompt_sha: string;
  allowed_tools: string[];
  intent: string | null;
  intent_source: string;
  corrections: string[];
  model_calls: ModelCall[];
  tools: ToolShape[];
}

export interface JobSummary {
  job_id: string;
  state: string;
  created_at: string;
  intent: string | null;
  outcome_kind: string | null;
  corrections: number;
  tokens_in: number;
  tokens_out: number;
  wall_ms: number;
  prompt_version: string | null;
  has_trace: boolean;
  feedback: string | null;
  failure_code: string | null;
}

export interface JobDetail extends JobSummary {
  policy_version: string;
  trace: { turns: TurnTrace[]; failure_code: string | null } | null;
  feedback_item: { id: string; verdict: string; reason: string | null } | null;
  snapshot: Record<string, unknown> | null;
}

export interface FeedbackItem {
  id: string;
  job_id: string;
  verdict: string;
  reason: string | null;
  intent: string | null;
  outcome_kind: string | null;
  status: string;
  created_at: string;
  has_snapshot: boolean;
}

export interface FeedbackDetail extends FeedbackItem {
  snapshot: Record<string, unknown> | null;
}

export interface JobFilters {
  state?: string;
  hasCorrection?: boolean;
  hasFeedback?: boolean;
}

/** A refusal the panel treats as "not for this operator". */
export class InspectorDenied extends Error {}

/** HTTP 401: the operator session is gone (8 h TTL, no refresh). */
export class InspectorSessionExpired extends InspectorDenied {}

export interface InspectorApi {
  listJobs: (
    filters: JobFilters,
    cursor?: string | null,
  ) => Promise<{ items: JobSummary[]; next_cursor: string | null }>;
  jobDetail: (jobId: string) => Promise<JobDetail>;
  listFeedback: (status?: string) => Promise<FeedbackItem[]>;
  feedbackDetail: (id: string) => Promise<FeedbackDetail>;
  setFeedbackStatus: (id: string, status: string) => Promise<FeedbackItem>;
}

export function createInspectorApi(
  fetcher: typeof fetch = fetch,
): InspectorApi {
  const call = async <T>(path: string, init?: RequestInit): Promise<T> => {
    const response = await fetcher(`/operator/assistant${path}`, {
      ...init,
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
    if (response.status === 401) {
      throw new InspectorSessionExpired("401");
    }
    if (response.status === 403) {
      throw new InspectorDenied("403");
    }
    if (!response.ok) {
      throw new Error(`inspector_http_${String(response.status)}`);
    }
    return (await response.json()) as T;
  };
  const query = (values: Record<string, string | undefined>): string => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(values)) {
      if (value !== undefined && value !== "") params.set(key, value);
    }
    const text = params.toString();
    return text ? `?${text}` : "";
  };
  return {
    listJobs: (filters, cursor) =>
      call(
        `/jobs${query({
          state: filters.state,
          has_correction:
            filters.hasCorrection === undefined
              ? undefined
              : String(filters.hasCorrection),
          has_feedback:
            filters.hasFeedback === undefined
              ? undefined
              : String(filters.hasFeedback),
          cursor: cursor ?? undefined,
          limit: "25",
        })}`,
      ),
    jobDetail: (jobId) => call(`/jobs/${encodeURIComponent(jobId)}`),
    listFeedback: (status) =>
      call(`/feedback${query({ status, limit: "50" })}`),
    feedbackDetail: (id) => call(`/feedback/${encodeURIComponent(id)}`),
    setFeedbackStatus: (id, status) =>
      call(`/feedback/${encodeURIComponent(id)}/status`, {
        method: "POST",
        body: JSON.stringify({ status }),
      }),
  };
}
